import React, { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react"
//import { useLocation } from "@reach/router"
import { window } from "browser-monads"
import parse from 'html-react-parser';
import isEqual from 'react-fast-compare'
import useIFrameResizer from "components/utils/useIFrameResizer"

import {
  defaultValues,
  dataStore,
  layoutStore,
  locationStore,
  mapStore,
  pageStore,
} from "state/store-zustand";

import SEO from "components/seo"
import BadgeAction from "components/elements/badge/badgeAction"
import Breadcrumbs from "components/elements/breadcrumbs"
import ShowMore from "components/elements/showMore"
import Vibes from "components/elements/vibes"

import GalleryModal from "components/modal/place"
import GridGalleryView from "components/elements/gallery/gridGallery"

import LoadingProfile from "components/elements/loading/loadingProfile"
import Skeleton from "react-loading-skeleton"
import TimeLocation from "components/map/timeLocationPlace"

const ListStories = lazy(() => import("components/post/listStories"))
const ShareButtons = lazy(() => import("components/post/shareButtons"))

const EventCards = lazy(() => import("components/elements/cards/eventCards"))
import { eventsToCards } from "components/post/blocks/singCardsBlock/placesToCards"
import TipsCard from "./tips"

//import { RelatedPlacesCard } from "./relatedPlacesCards"
import { getRelatedPlaces } from "components/utils/getRelatedPlaces"
import { placesToCards } from "components/post/blocks/singCardsBlock/placesToCards"
const SuggestedCards = lazy(() => import("components/elements/cards/suggestedCards"))

import { linksToBreadcrumbs } from "components/utils/schemaUtils"
import { fetchPlacesDetails } from "vibemap-constants/dist/helpers"
import { segmentize } from "components/utils/routeUtils"

import "styles/pages.scss"
import "styles/details.scss"

const PlaceDetails = ({
  data,
  pageContext,
  pageResources,
  serverData = {},
  params,
  id,
  seoSuffix = "",
  detailsOnly = false,
  eventsOnly = false,
  mapOnly = false,
  showBadge = true,
  showMobileView = false,
  showSuggested = false,
  showTopTip = false,
  showStories = false,
  slugFromURL = null,
  ...props
}) => {

  const urlLocation = window.location;
  const urlSearch = new URLSearchParams(urlLocation.search);
  const urlParams = Object.fromEntries(urlSearch.entries());

  const placeCurrent = dataStore(state => state.placeCurrent)
  const detailsLoading = dataStore(state => state.detailsLoading)
  const setPlaceCurrent = dataStore(state => state.setPlaceCurrent)
  const placeCardsSuggested  = dataStore(state => state.placeCardsSuggested)
  const setPlaceCardsSuggested =  dataStore(state => state.setPlaceCardsSuggested)
  const setDetailsLoading = dataStore(state => state.setDetailsLoading)

  const showNote = mapStore(state => state.showNote)
  const noteClass = showNote ? 'note-show' : 'note-hide'

  const setHeight = layoutStore(state => state.setHeight)

  const embedded = pageStore(state => state.embedded)
  const isClient = pageStore(state => state.isClient)
  const pageTheme = pageStore((state) => state.theme)

  const zoomCurrent = locationStore((state) => state.zoomCurrent)
  const zoomPrevious = locationStore((state) => state.zoomPrevious)
  const setZoomCurrent = locationStore((state) => state.setZoomCurrent)

  // ⚡Initialize from server/link, if available
  // TODO: Move to SSR/Broswer logic
  const [placeDetails, setPlaceDetails] = useState(
    serverData?.place
      ? serverData?.place
      : urlLocation?.state?.place
        ? urlLocation?.state.place
        : placeCurrent
  ) // TODO: Set default from SSR data

  // If URL parmas contain place, set that data initially
  const isTitleCard = placeCurrent && placeCurrent?.id != undefined && placeCurrent?.is_title_card != undefined
  const preloaded = serverData?.preloaded || isTitleCard
  if (urlParams.name) {
    serverData.place = {
      id: id,
      properties: urlParams,
    }
  }

  // ⚡ If the place changes, reload the details
  const placeCurrentDep = JSON.stringify(placeCurrent)
  useEffect(() => {
    const hasChanged = !isEqual(placeCurrent, placeDetails)
    if (placeCurrent && hasChanged) {
      //console.log('DEBUG hasChanged ', placeDetails, placeCurrent);
      setPlaceDetails(placeCurrent)
    }
  }, [placeCurrentDep])


  const containerRef = useRef(null)
  const iframeResizer = useIFrameResizer({ containerRef: containerRef })
  const [hasResizer, parent] = iframeResizer

  const containerClass = hasResizer
    ? 'container container-resizable'
    : 'container'

  const [links, setLinks] = useState([])
  const [breadcrumbItems, setBreadcrumbItems] = useState()
  const [hasError, setHasError] = useState(false) // TODO: Set default from SSR data

  // Set ID from param or pre-fetched data
  const placeID = id
    ? id : params && params.id
      ? params.id
      : placeDetails && placeDetails.id
        ? placeDetails.id
        : null


  // TODO: also pre-render place if passed as a param
  // Safety check for nested properties
  const { properties } = placeDetails || {}
  const placeObject = properties
    ? properties
    : placeDetails

  const {
    address,
    categories,
    city,
    description,
    editorial_categories,
    hotspots_events,
    links: ticketLinks,
    location,
    name,
    opening_hours,
    is_open_24hrs,
    offers,
    price,
    slug,
    tips,
    vibes,
    vibemap_images,
    url,
  } = placeObject || {} // Safely destructure

  const title = `${name ? name + (seoSuffix ? seoSuffix : "") : seoSuffix} | Vibemap`

  // TODO: Make util
  // Load details from page slug or currently selected place
  const placeSlug = placeCurrent
    ? placeCurrent.slug
    : slugFromURL
      ? slugFromURL
      : slug
        ? slug
        : window && window.location.pathname.split("/").pop()


  useEffect(() => {
    // TODO: Does the data need to be fetched?
    const placeID = id
      ? id
      : placeSlug

    // TODO: Make hook or util
    console.log('DEBUG: Fetch place details ', placeID);
    const fetchData = async id => {
      setDetailsLoading(true)
      const details = await fetchPlacesDetails(placeID).catch(e => {
        console.log(`Error with place `, placeID, e)
        setHasError(true)
      })

      if (details) {
        console.log('DEBUG: Place details ', details.data);
        const placeData = details.data
        setPlaceDetails(placeData)
        setPlaceCurrent(placeData)
        setDetailsLoading(false)

        const relatedPlaces = await getRelatedPlaces(placeData?.id ? placeData.id : placeID)
        const cardsRelated = placesToCards(relatedPlaces?.results?.features);
        setPlaceCardsSuggested(cardsRelated)

        setTimeout(() => {
          const heightNew = document.querySelector('.page')?.scrollHeight
          console.log('DEBUG heightNew ', heightNew);
          setHeight(heightNew)
        }, 1000);

      } else {
        setHasError(true)
      }

    }

    // If no place details and not preloaded, load data
    if (preloaded != true || placeDetails == null) {
      fetchData(placeID)
    }
  }, [id, placeSlug])


  // Replace ID with Slug
  useEffect(() => {
    const alreadyHasSlug = urlLocation.pathname.includes(slug) == false
    const alreadyHasID = id && urlLocation.pathname.includes(id)
    const includeSearch = true
    // Get page and drop trailing slash
    const paths = segmentize(urlLocation.pathname)
    const search = urlLocation.search
    const page = urlLocation.pathname.replace(/\/$/, "")
    const currentPage = `/${paths[0]}`

    if (slug && alreadyHasSlug) {
      // If path has ID, replace with slug
      const newURL = includeSearch
        ? `${currentPage}/${slug}${search}`
        : `${currentPage}/${slug}`

      if (window !== undefined) {
        // TOOO: Set page title
        window.history.replaceState({}, '', newURL)
      }
    }
  }, [slug])


  // Add breadcrumbs
  useEffect(() => {
    const crumbs = []

    if (city) {
      crumbs.push({
        name: city.name,
        slug: city.slug,
        type: "city",
      })
    }

    /* TODO: Add breadcrumb from guide or list
    crumbs.push({
      name: `Events`,
      slug: `events`,
      type: 'index'
    })
    */

    const breadcrumbItems = linksToBreadcrumbs(crumbs)
    setLinks(crumbs)
    setBreadcrumbItems(breadcrumbItems)
  }, [])

  const breadcrumbs = <Breadcrumbs links={links} />
  const schemaData = []
  // TODO: add schema:
  // const eventSchema = eventDetails && eventToSchema(eventDetails)
  // if (eventSchema) {
  //   schemaData.push(eventSchema)
  // }

  if (breadcrumbItems) {
    schemaData.push(breadcrumbItems)
  }

  const images_parsed = vibemap_images && typeof (vibemap_images) == 'string'
    ? JSON.parse(vibemap_images)
    : vibemap_images
  const images = images_parsed && images_parsed.length > 0
    ? images_parsed.map(image => (
      <img className={"photo"} src={image?.original} />
    ))
    : []

  const topTip = tips && tips.length > 0 ? tips[0] : null

  // TODO: Has booking button
  const isVisit = categories && categories.some(category => category.toLowerCase() == "visit")
  const showButton = (ticketLinks && ticketLinks.length > 0) || isVisit
  const firstLink = ticketLinks && ticketLinks[0]
  const buttonAction = firstLink ? ticketLinks[0]["action"] : "visit"
  const buttonURL = firstLink ? ticketLinks[0]["url"] : url

  const columnLeft = showMobileView || eventsOnly ? `is-12-desktop` : `is-7-desktop`
  const columnRight = showMobileView || mapOnly ? `is-12-desktop` : `is-5-desktop`

  const placeType =
    categories && categories.includes("Visit") ? "experience" : "place"

  const pageLink = `/places/details/${slug ? slug : placeID}`
  const canonical = `https://vibemap.com${pageLink}`

  const MultiSkeleton = <Skeleton count={3} />

  const showDetails = isClient && placeDetails && !detailsLoading

  const classDetails = `place post-body ${detailsOnly ? "details" : "full"} ${embedded ? 'embedded' : ''}`
  const classColumns = `place-details columns ${detailsOnly ? "is-multiline" : "is-flex-tablet"}`
  const classColumnLeft = `column left ${columnLeft} is-12-tablet`
  const classDescription = `column left ${columnLeft} is-12-tablet description`
  const classLocation = `column right ${columnRight} is-12-tablet location no-padding`

  const onBack = () => {
    setPlaceCurrent(null)
    setZoomCurrent(zoomPrevious)
    // Only navigate if not in iframe
    const has_history = window.history.length > 1
    if (has_history) {
      window.history.back(-1)
    }
  }



  useEffect(() => {
    window.addEventListener('popstate', () => {
      setPlaceCurrent(null)
      //navigate(-1)
    });

    if (zoomCurrent != defaultValues.zoomCurrent) {
      // Use details default zoom if current zoom is less than details default
      const zoomDetails = defaultValues.zoomDetails <= zoomCurrent
        ? zoomCurrent
        : defaultValues.zoomDetails

      setZoomCurrent(zoomDetails)
    }

  }, [])

  const hasBadge = showBadge && editorial_categories && editorial_categories.length > 0
  const badge = hasBadge
    ? <BadgeAction
        badges={editorial_categories}
        offers={offers} />
    : null

  const descLength = description?.length
  const maxLength = 600
  const descriptionHtml = description && typeof description === 'string'
    ? (
      <div className={`description ${noteClass}`}>
        {parse(description)}
      </div>
    )
    : null

  const showDetailsBasic = mapOnly == false && eventsOnly == false
  const showMap = mapOnly == true || eventsOnly == false

  const hasEvents = hotspots_events?.features && hotspots_events?.features.length > 0

  const eventsDep = JSON.stringify(hotspots_events?.features)
  //console.log('Events changed ', hotspots_events);

  const eventCards = useMemo(() => {
    return hasEvents ? eventsToCards(hotspots_events?.features) : null;
  }, [hasEvents, eventsDep]);


  const placeLink = `/places/details/${slug ? slug : placeID}`
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  return (
    <div className={containerClass} ref={containerRef}>
      <SEO
        canonical={canonical}
        lang="en-US"
        title={title}
        //data={data.allWpPage.edges[0].node.seo}
        schemaData={schemaData}
      />

      {/* TODO: Is loading */}
      <div className={classDetails}>
        {showDetailsBasic
          ? <GridGalleryView
              placeLink={placeLink}
              openModal={openModal}
              onBack={onBack}
              images={images_parsed}
              name={name}
              slug={slug}
              start_date={placeDetails?.start_date || placeDetails?.updated_at || ""}
              category={categories?.length > 0 ? categories[0] : ""}
              type={placeDetails?.tpe}
            />
          : null
        }

        {detailsLoading && <LoadingProfile />}
        <div className={classColumns}>
          {hasError && (
            <div className={classColumnLeft}>
              <div>There's a problem displaying this. Please reload or try again later. </div>
            </div>
          )}

          {showDetails && (
            <>
              {mapOnly == false
                ? (
                  <div className={classDescription}>
                    {showDetailsBasic
                      ? (
                        <>
                          <Vibes numVibesShown={7} vibes={vibes} size={'1.2rem'} />
                          <div className="section">
                            {badge}
                            <p>
                              {description?.length > maxLength
                                ? <ShowMore className='description'>{descriptionHtml}</ShowMore>
                                : descriptionHtml
                              }
                            </p>
                          </div>
                        </>
                      )
                      : null
                    }

                    {showStories
                      ? <Suspense fallback={MultiSkeleton}>
                          <ListStories placeId={id} />
                        </Suspense>
                      : null
                    }

                    {hotspots_events && hotspots_events?.features.length > 0
                      ? <Suspense fallback={MultiSkeleton}>
                          <EventCards
                            eventCards={eventCards}
                            style='carousel'
                            numColumns={showMobileView ? 1.3 : undefined} />
                        </Suspense>
                      : null
                    }

                    {showDetailsBasic && topTip ? (
                      <TipsCard tips={tips} header="Tips" />
                    ) : null}

                  </div>
                )
                : null
              }

              {showDetails && showMap
                ? (
                  <div className={classLocation} >
                      <TimeLocation
                        buttonAction={buttonAction}
                        buttonURL={buttonURL}
                        showButton={showButton}
                        details={placeDetails}
                        placeType={placeType}
                      />
                  </div>
                )
                : null
              }
            </>
          )}

          {showTopTip && showDetailsBasic && topTip ? (
            <div className="section">
              <blockquote>{topTip}</blockquote>
            </div>
          ) : null // 'Add the first tip...'
          }

          {showSuggested && placeCardsSuggested && placeCardsSuggested.length > 0
            ? <Suspense fallback={MultiSkeleton}>
              <SuggestedCards showAsMobile={showMobileView} />
            </Suspense>
            : null
          }

        </div>

        <GalleryModal
          isOpen={isModalOpen}
          imageGallery={images_parsed}
          onClose={closeModal}
        />

        <section>
          <Suspense fallback={<span>...</span>}>
            <ShareButtons
              fallback={<div>Loading social...</div>}
              className="desktop-share-buttons"
              url={window.location.href}
            />
          </Suspense>
        </section>
      </div>
    </div>
  )
}
export default PlaceDetails

/*
export async function getServerData({ params, ...props}) {
  const { id } = params
  const errorResponse = {
    status: 500,
    headers: {},
    props: { preloaded: true, place: null }
  }

  try {
    const response = await fetchPlacesDetails(id)
      .catch(e => {
        console.log(`Error with place `, id, e)
        setHasError(true)
      })


    const details = response?.data
    console.log('Place details SSR ', details);
    if (details) {
      return {
       props: {
        preloaded: true,
        place: details
       },
      }
    } else {
      return errorResponse
    }

  } catch (error) {
    console.log('error ', error);
    return errorResponse
  }
}
*/
